import React, { useEffect, useMemo, useState } from 'react';
import { Button, Dropdown, Form, Header, Icon, Modal } from 'semantic-ui-react';
import { useTranslation } from 'react-i18next';
import { API, showError, showSuccess } from '../helpers';

/**
 * 模型梯队配置弹窗
 *
 * 从运营设置迁移到渠道管理页面，方便在渠道列表旁直接配置。
 * 底层仍为全局配置 ModelTierConfig，对所有渠道生效。
 *
 * 修改时间：2026-08-19
 */
const ModelTierModal = ({ open, onClose }) => {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);
  const [modelTier, setModelTier] = useState([]);
  const [tierDraft, setTierDraft] = useState('');
  const [tierModelOptions, setTierModelOptions] = useState([]);
  const [originTier, setOriginTier] = useState([]);

  useEffect(() => {
    if (!open) return;
    loadTierConfig();
    loadTierModelOptions();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const loadTierConfig = async () => {
    try {
      const res = await API.get('/api/option/');
      const { success, data } = res.data;
      if (success) {
        const item = data.find((d) => d.key === 'ModelTierConfig');
        const raw = item ? item.value : '[]';
        try {
          const parsed = JSON.parse(raw) || [];
          setModelTier(parsed);
          setOriginTier(parsed);
        } catch {
          setModelTier([]);
          setOriginTier([]);
        }
      }
    } catch (e) {
      // ignore
    }
  };

  const loadTierModelOptions = async () => {
    try {
      const res = await API.get('/api/channel/models');
      const opts = (res.data.data || []).map((m) => ({
        key: m.id,
        text: m.id,
        value: m.id,
      }));
      setTierModelOptions(opts);
    } catch (e) {
      // 加载失败时仍可手动输入
    }
  };

  const addTierModel = () => {
    const model = tierDraft.trim();
    if (model === '') return;
    if (modelTier.includes(model)) {
      setTierDraft('');
      return;
    }
    setModelTier([...modelTier, model]);
    setTierDraft('');
  };

  const moveTierModel = (index, direction) => {
    const next = [...modelTier];
    const target = index + direction;
    if (target < 0 || target >= next.length) return;
    [next[index], next[target]] = [next[target], next[index]];
    setModelTier(next);
  };

  const removeTierModel = (index) => {
    setModelTier(modelTier.filter((_, i) => i !== index));
  };

  // 合并已注册模型与已加入梯队的模型，保证下拉里都能显示
  const tierDropdownOptions = useMemo(() => {
    const opts = [...tierModelOptions];
    modelTier.forEach((m) => {
      if (!opts.find((o) => o.value === m)) {
        opts.push({ key: m, text: m, value: m });
      }
    });
    return opts;
  }, [tierModelOptions, modelTier]);

  const handleSave = async () => {
    const tierJSON = JSON.stringify(modelTier);
    setLoading(true);
    try {
      const res = await API.put('/api/option/', {
        key: 'ModelTierConfig',
        value: tierJSON,
      });
      const { success, message } = res.data;
      if (success) {
        showSuccess(t('setting.operation.model_tier.save') + ' ✓');
        setOriginTier(modelTier);
        onClose();
      } else {
        showError(message);
      }
    } catch (e) {
      showError(e.message);
    }
    setLoading(false);
  };

  const hasChanges = JSON.stringify(modelTier) !== JSON.stringify(originTier);

  // 根据当前梯队数据动态生成优先级与各情形说明
  const renderTierExample = () => {
    if (modelTier.length === 0) {
      return null;
    }
    const priorityLine = modelTier.join(' > ');
    const scenarios = [];
    if (modelTier.length === 1) {
      scenarios.push(
        t('setting.operation.model_tier.ex_direct', {
          model: modelTier[0],
          provides: modelTier[0],
        })
      );
    } else {
      scenarios.push(
        t('setting.operation.model_tier.ex_fallback', {
          requested: modelTier[0],
          provides: modelTier.slice(1).join(', '),
          fallback: modelTier[1],
        })
      );
      scenarios.push(
        t('setting.operation.model_tier.ex_direct', {
          model: modelTier[1],
          provides: modelTier[1],
        })
      );
      if (modelTier.length >= 3) {
        const last = modelTier[modelTier.length - 1];
        scenarios.push(
          t('setting.operation.model_tier.ex_fallback', {
            requested: modelTier[0],
            provides: last,
            fallback: last,
          })
        );
      }
    }
    return (
      <div
        style={{
          background: '#f8fafc',
          border: '1px solid rgba(24,24,27,0.06)',
          borderRadius: 10,
          padding: '12px 14px',
          marginBottom: 12,
        }}
      >
        <div style={{ fontSize: 12, fontWeight: 700, color: '#18181b', marginBottom: 8 }}>
          {t('setting.operation.model_tier.priority')}: {priorityLine}
        </div>
        <div style={{ fontSize: 12, fontWeight: 700, color: '#6b7280', marginBottom: 4 }}>
          {t('setting.operation.model_tier.example_title')}
        </div>
        <ul style={{ margin: 0, paddingLeft: 18, color: '#52525b', fontSize: 12, lineHeight: 1.7 }}>
          {scenarios.map((s, i) => (
            <li key={i}>{s}</li>
          ))}
        </ul>
      </div>
    );
  };

  return (
    <Modal open={open} onClose={onClose} size='small' closeIcon className='studio-modal'>
      <Header as='h2' style={{ fontSize: 18, fontWeight: 700, color: '#18181b', marginBottom: 0 }}>
        <Icon name='sort amount down' />
        {t('setting.operation.model_tier.title')}
      </Header>
      <Modal.Content>
        <p style={{ color: 'gray', fontSize: '12px', marginBottom: 12 }}>
          {t('setting.operation.model_tier.description')}
        </p>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 12 }}>
          <Dropdown
            fluid
            selection
            search
            allowAdditions
            additionLabel={t('setting.operation.model_tier.addition_label')}
            options={tierDropdownOptions}
            value={tierDraft}
            onAddItem={(e, { value }) =>
              setTierModelOptions((prev) => [...prev, { key: value, text: value, value }])
            }
            onChange={(e, { value }) => setTierDraft(value)}
            placeholder={t('setting.operation.model_tier.placeholder')}
            style={{ flex: 1 }}
          />
          <Button
            icon='plus'
            content={t('setting.operation.model_tier.add')}
            disabled={tierDraft.trim() === ''}
            onClick={addTierModel}
            style={{ borderRadius: 10, fontWeight: 600, whiteSpace: 'nowrap' }}
          />
        </div>
        {modelTier.length === 0 ? (
          <p style={{ color: 'gray', fontSize: '13px' }}>
            {t('setting.operation.model_tier.empty')}
          </p>
        ) : (
          <ol style={{ listStyle: 'none', padding: 0, margin: '0 0 12px' }}>
            {modelTier.map((model, index) => (
              <li
                key={`${model}-${index}`}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 12,
                  padding: '8px 12px',
                  border: '1px solid rgba(24,24,27,0.08)',
                  borderRadius: 10,
                  marginBottom: 8,
                  background: '#fff',
                }}
              >
                <span
                  style={{
                    flex: '0 0 28px',
                    height: 28,
                    borderRadius: 8,
                    background: 'linear-gradient(135deg, #4f63a8, #287d78)',
                    color: '#fff',
                    fontSize: 13,
                    fontWeight: 700,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  {index + 1}
                </span>
                <span
                  title={model}
                  style={{
                    flex: 1,
                    minWidth: 0,
                    color: '#18181b',
                    fontSize: 14,
                    fontWeight: 500,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {model}
                </span>
                <span style={{ display: 'flex', gap: 4 }}>
                  <Button
                    size='mini'
                    icon
                    disabled={index === 0}
                    onClick={() => moveTierModel(index, -1)}
                    title={t('setting.operation.model_tier.move_up')}
                  >
                    <Icon name='arrow up' />
                  </Button>
                  <Button
                    size='mini'
                    icon
                    disabled={index === modelTier.length - 1}
                    onClick={() => moveTierModel(index, 1)}
                    title={t('setting.operation.model_tier.move_down')}
                  >
                    <Icon name='arrow down' />
                  </Button>
                  <Button
                    size='mini'
                    icon
                    onClick={() => removeTierModel(index)}
                    title={t('setting.operation.model_tier.remove')}
                  >
                    <Icon name='close' style={{ color: '#dc2626' }} />
                  </Button>
                </span>
              </li>
            ))}
          </ol>
        )}
        {renderTierExample()}
      </Modal.Content>
      <Modal.Actions
        style={{ background: '#f5f5f5', borderTop: '1px solid rgba(0,0,0,0.08)', padding: '14px 24px' }}
      >
        <Button onClick={onClose} style={{ borderRadius: 10, fontWeight: 600 }}>
          取消
        </Button>
        <Button
          primary
          loading={loading}
          disabled={!hasChanges}
          onClick={handleSave}
          icon='check'
          content={t('setting.operation.model_tier.save')}
          style={{ borderRadius: 10, fontWeight: 600 }}
        />
      </Modal.Actions>
    </Modal>
  );
};

export default ModelTierModal;
